import { List } from '@ksyos/design-system';
import { useSearchParams } from '@ksyos/router';
import Row from 'react-bootstrap/Row';
import { SomnologyOrganizationType } from '../../../../common/enums/SomnologyOrganizationType';
import { translateKey } from '../../../translate';
import { ChapterTitle } from '../../elements/atoms/chapterTitle/ChapterTitle';
import { ExternalLink } from '../../elements/atoms/externalLink/ExternalLink';
import { Figure } from '../../elements/atoms/figure/Figure';
import { HeroText } from '../../elements/atoms/heroText/HeroText';
import { PageTitle } from '../../elements/atoms/pageTitle/PageTitle';
import { Paragraph } from '../../elements/atoms/paragraph/Paragraph';
import { ParagraphTitle } from '../../elements/atoms/paragraphTitle/ParagraphTitle';
import { SubParagraphTitle } from '../../elements/atoms/subParagraphTitle/SubParagraphTitle';
import { Alert } from '../../elements/molecules/alert/Alert';
import { Article } from '../../elements/molecules/article/article';
import { Container } from '../../elements/molecules/container/Container';
import { Content } from '../../elements/molecules/content/Content';
import { ImageCarousel } from '../../elements/molecules/imageCarousel/ImageCarousel';
import { Main } from '../../elements/molecules/main/Main';
import { PageSection } from '../../elements/molecules/pageSection/PageSection';
import { DeprecatedOsasFaqCard } from '../../elements/organisms/faqCards/DeprecatedOsasFaqCard';
import { Footer } from '../../elements/organisms/footer/Footer';
import { Header } from '../../elements/organisms/header/Header';
import { PageWrapper } from '../../elements/organisms/pageWrapper/PageWrapper';

function nsiInstructions() {
    const stepOne = [
        {
            altText: 'Stap 1.1: De borstplakker uitpakken',
            caption:
                'Haal de borstplakker door de mouw tot aan de nekopening en verwijder het witte papiertje van de achterkant van de sensor.',
            captionNumber: '1.1',
            src: '/assets/images/somnology/instruction/stap_1_1.jpg',
        },
        {
            altText: 'Stap 1.2: De borstplakker aanbrengen',
            caption:
                'Plak de borstplakker boven aan het borstbeen, net onder het kuiltje. Plak een stukje tape over de borstplakker, zodat deze goed blijft zitten.',
            captionNumber: '1.2',
            src: '/assets/images/somnology/instruction/stap_1_2.jpg',
        },
    ];

    const stepTwo = [
        {
            altText: 'Stap 2: De slaaprecorder aanbrengen',
            caption: 'Doe het horloge om je niet-dominante pols. Ben je rechtshandig, dan om je linkerpols.',
            captionNumber: '2.1',
            src: '/assets/images/somnology/instruction/stap_2.jpg',
        },
    ];

    const stepThree = [
        {
            altText: 'Stap 3: De vingersonde aanbrengen',
            caption:
                'Steek je wijsvinger helemaal tot het einde van de vingersonde. Het is normaal dat je een lichte druk voelt.',
            captionNumber: '3.1',
            src: '/assets/images/somnology/instruction/stap_3_1.jpg',
        },
        {
            altText: 'Stap 3: De vingersonde aanbrengen',
            caption:
                'Trek het folie waarop ‘TOP’ staat uit de vingersonde terwijl je het einde van de vingersonde tegen een hard oppervlak duwt.',
            captionNumber: '3.2',
            src: '/assets/images/somnology/instruction/stap_3_2.jpg',
        },
    ];
    const stepFour = [
        {
            altText: 'Stap 4: Het apparaat aanzetten',
            caption:
                'Druk een paar seconden op de ronde knop totdat het scherm oplicht. De slaaprecorder voert nu een korte test uit van ongeveer 3 minuten.',
            captionNumber: '4.1',
            src: '/assets/images/somnology/instruction/stap_4_1.jpg',
        },
        {
            altText: 'Stap 4: Het apparaat aanzetten',
            caption:
                'Wanneer je ‘OK’ of ‘GOOD NIGHT’ in het beeld ziet verschijnen is de meting gestart. Het scherm gaat na 1 minuut vanzelf uit.',
            captionNumber: '4.2',
            src: '/assets/images/somnology/instruction/stap_4_2.jpg',
        },
    ];

    return (
        <>
            <PageSection>
                <Article>
                    <PageTitle>Instructies voor de slaaprecorder</PageTitle>
                    <HeroText>
                        Je bent door de huisarts via Ksyos verwezen naar het{' '}
                        <ExternalLink href="https://www.nederlandsslaapinstituut.nl/">
                            Nederlands Slaap Instituut
                        </ExternalLink>
                        . Bekijk hier de instructies voor het onderzoek met de slaaprecorder.
                    </HeroText>
                </Article>
            </PageSection>
            <PageSection bgColor="white">
                <Article>
                    <Content>
                        <ChapterTitle>Voorafgaand aan het slaaponderzoek</ChapterTitle>
                        <List styleType="bullet">
                            <List.Item>
                                Verwijder ringen, horloge en andere sieraden van je niet-dominante hand (als je
                                rechtshandig bent verwijder je sieraden van je linkerhand).
                            </List.Item>
                            <List.Item>
                                Zorg ervoor dat je vingernagels kort geknipt zijn. Kunstnagels en nagellak dien je te
                                verwijderen.
                            </List.Item>
                            <List.Item>
                                Voor personen met borsthaar: scheer de plek waar de borstplakker wordt opgeplakt.
                            </List.Item>
                        </List>
                        <Alert
                            className="mb-0"
                            variant="info"
                        >
                            <Paragraph>
                                <strong>Let op:</strong> de slaaprecorder hoort uit te staan. Indien dit niet het geval
                                is, ga terug naar je huisarts. Zet de slaaprecorder pas aan nadat je onderstaande
                                instructies hebt gevolgd.
                            </Paragraph>
                        </Alert>
                    </Content>
                    <Content>
                        <ParagraphTitle>Twee Slaaprecorders</ParagraphTitle>
                        <Paragraph>
                            {' '}
                            Er zijn twee soorten slaaprecorders. In praktijk hebben ze dezelfde werking. Gebruik de
                            ronde knop zoals bij stap vier staat beschreven.
                        </Paragraph>
                        <Figure
                            alt="Twee Slaaprecorders"
                            className="mb-0"
                            imageUrl="/assets/images/somnology/instruction/twee_slaaprecorders.jpg"
                            imgClassName="w-100"
                        />
                    </Content>
                </Article>
            </PageSection>
            <PageSection bgColor="white">
                <Article>
                    <Content>
                        <ChapterTitle>De slaaprecorder gebruiken</ChapterTitle>
                        <ParagraphTitle>Stap 1: De borstplakker aanbrengen</ParagraphTitle>
                        <ImageCarousel items={stepOne} />
                    </Content>
                    <Content className="d-none d-lg-block">
                        <SubParagraphTitle>Overzicht stap 1: borstplakker</SubParagraphTitle>
                        <List>
                            <List.Item>
                                1.1 Haal de borstplakker door de mouw tot aan de nekopening en verwijder het witte
                                papiertje van de achterkant van de sensor.
                            </List.Item>
                            <List.Item>
                                1.2 Plak de borstplakker boven aan het borstbeen, net onder het kuiltje. Plak een stukje
                                tape over de borstplakker, zodat deze goed blijft zitten.
                            </List.Item>
                        </List>
                    </Content>
                </Article>
                <Article>
                    <Content>
                        <ParagraphTitle>Stap 2: De slaaprecorder aanbrengen</ParagraphTitle>
                        <ImageCarousel items={stepTwo} />
                    </Content>
                    <Content className="d-none d-lg-block">
                        <SubParagraphTitle>Overzicht stap 2</SubParagraphTitle>
                        <List>
                            <List.Item>
                                2.1 Doe het horloge om je niet-dominante pols. Ben je rechtshandig, dan om je
                                linkerpols.
                            </List.Item>
                        </List>
                    </Content>
                </Article>
                <Article>
                    <Content>
                        <ParagraphTitle>Stap 3: De vingersonde aanbrengen</ParagraphTitle>
                        <ImageCarousel items={stepThree} />
                    </Content>
                    <Content className="d-none d-lg-block">
                        <SubParagraphTitle>Overzicht stap 3</SubParagraphTitle>
                        <List>
                            <List.Item>
                                3.1 Steek je wijsvinger helemaal tot het einde van de vingersonde. Het is normaal dat je
                                een lichte druk voelt.
                            </List.Item>
                            <List.Item>
                                3.2 Trek het folie waarop ‘TOP’ staat uit de vingersonde terwijl je het einde van de
                                vingersonde tegen een hard oppervlak duwt.
                            </List.Item>
                        </List>
                    </Content>
                </Article>
                <Article>
                    <Content>
                        <ParagraphTitle>Stap 4: Het apparaat aanzetten</ParagraphTitle>
                        <ImageCarousel items={stepFour} />
                    </Content>
                    <Content className="d-none d-lg-block">
                        <SubParagraphTitle>Overzicht stap 4</SubParagraphTitle>
                        <List>
                            <List.Item>
                                4.1 Druk een paar seconden op de ronde knop totdat het scherm oplicht. De slaaprecorder
                                voert nu een korte test uit van ongeveer 3 minuten.
                            </List.Item>
                            <List.Item>
                                4.2 Wanneer je ‘OK’ of ‘GOOD NIGHT’ in het beeld ziet verschijnen is de meting gestart.
                                Het scherm gaat na 1 minuut vanzelf uit.
                            </List.Item>
                        </List>
                    </Content>
                    <Content>
                        <Alert
                            className="mb-0"
                            variant="info"
                        >
                            <Paragraph>
                                <strong>Let op:</strong> Wanneer er zich tijdens het onderzoek een probleem voordoet zie
                                je op het scherm een kruis of ‘TEST ABORTED’. Noteer in dat geval de foutcode en
                                informeer de volgende dag jouw arts of de assistente.
                            </Paragraph>
                        </Alert>
                    </Content>
                </Article>
            </PageSection>

            <PageSection bgColor="white">
                <Article>
                    <ChapterTitle>Na het slaaponderzoek</ChapterTitle>
                    <Paragraph>
                        De ochtend nadat je het slaaponderzoek hebt gedaan mag je de slaaprecorder, vingersonde en
                        borstplakker loshalen. Je hoeft de slaaprecorder niet uit te zetten, dit gaat vanzelf. Je levert
                        de slaaprecorder in bij je eigen huisartsenpraktijk. Zij zullen het onderzoek opsturen naar de
                        specialisten van het NSI. Indien het onderzoek goed is verlopen, wordt er binnen 2 weken contact
                        met je opgenomen voor het inplannen van een videoconsult. Het videoconsult met de medisch
                        specialist en de bevindingen uit het slaaponderzoek zijn beide noodzakelijk om tot de
                        uiteindelijke diagnose te komen.
                    </Paragraph>
                    <Paragraph>
                        Mocht de slaaprecorder onvoldoende bruikbare data hebben opgenomen, word je door je huisarts
                        gevraagd om nog een nacht met de slaaprecorder te slapen.{' '}
                    </Paragraph>
                </Article>
            </PageSection>
        </>
    );
}

function ksyosInstructions() {
    return (
        <>
            <PageSection>
                <Article>
                    <PageTitle>Instructies voor de slaaprecorder</PageTitle>
                    <HeroText>
                        Je bent door de huisarts verwezen naar Ksyos voor een slaaponderzoek. Bekijk hier de instructies
                        voor het onderzoek met de slaaprecorder.
                    </HeroText>
                </Article>
            </PageSection>

            <PageSection bgColor="white">
                <Article>
                    <Content>
                        <ChapterTitle>Voorafgaand aan het onderzoek</ChapterTitle>
                        <Paragraph>
                            Je hebt van je huisarts, of per post van Ksyos, de slaaprecorder gekregen. Het etui bestaat
                            uit de volgende onderdelen:
                        </Paragraph>
                        <Figure
                            alt="Twee Slaaprecorders"
                            className="mb-0"
                            imageUrl="/assets/images/somnology/instruction/night_owl_overview.jpg"
                            imgClassName="w-100"
                        />
                    </Content>
                    <Content>
                        <List
                            counterColor="secondary"
                            counterFontWeight="medium"
                            styleType="counter"
                        >
                            <List.Item>Sensor</List.Item>
                            <List.Item>Sensor</List.Item>
                            <List.Item>Oplaadstation</List.Item>
                            <List.Item>Bevestigingsplakker vinger</List.Item>
                            <List.Item>Bevestigingsplakker voorhoofd</List.Item>
                            <List.Item>Verloopstukje micro-usb kabel</List.Item>
                            <List.Item>Verloopstukje iPhone kabel</List.Item>
                        </List>
                    </Content>
                    <Content>
                        <List styleType="bullet">
                            <List.Item>
                                Je hebt een mail ontvangen van Ectosense met daarin de instructies voor het
                                slaaponderzoek. Volg de stappen uit deze mail.
                            </List.Item>
                            <List.Item>
                                Zorg dat beide sensoren voldoende zijn opgeladen. Dit kan je controleren door de
                                sensoren te verbinden met de app. Het batterijpercentage wordt dan per sensor
                                weergegeven.
                            </List.Item>
                            <List.Item>
                                Om de sensoren op te laden leg je de sensor op het bijgeleverde oplaadstation en sluit
                                deze aan op de stroom. Wanneer de sensor aan het opladen is, brandt er een blauw lampje
                                op het oplaadstation. Laad de sensor minimaal een uur op om 1 nacht te kunnen opnemen.
                                Wanneer het blauwe lampje stopt met branden is de sensor volledig opgeladen.
                            </List.Item>
                        </List>
                    </Content>
                    <Content>
                        <Figure
                            alt="Twee Slaaprecorders"
                            className="mb-0"
                            imageUrl="/assets/images/somnology/instruction/night_owl_detail.png"
                            imgClassName="w-100"
                        />
                    </Content>
                    <Content>
                        <List
                            counterColor="secondary"
                            counterFontWeight="medium"
                            styleType="counter"
                        >
                            <List.Item>Oplaadkabel</List.Item>
                            <List.Item>Verloopstukje</List.Item>
                            <List.Item>Oplaadstation</List.Item>
                            <List.Item>Sensor</List.Item>
                            <List.Item>LED</List.Item>
                        </List>
                    </Content>
                    <Content>
                        <List styleType="bullet">
                            <List.Item>
                                Zorg dat je telefoon aan de stroom ligt gedurende de nacht én binnen 2 meter van de
                                sensoren afligt. De sensoren zijn namelijk via bluetooth verbonden met de app om de data
                                door te kunnen sturen naar Ksyos.
                            </List.Item>
                        </List>
                    </Content>
                </Article>
            </PageSection>

            <PageSection bgColor="white">
                <Article>
                    <Content>
                        <ChapterTitle>Na het slaaponderzoek</ChapterTitle>
                        <Paragraph>
                            Zodra je voldoende nachten met de slaaprecorder hebt geslapen dien je de slaaprecorder,
                            inclusief oplader, weer in te leveren bij je eigen huisartsenpraktijk. Als je de
                            slaaprecorder per post hebt ontvangen dan stuur je deze retour naar Ksyos middels het
                            meegeleverde retourformulier.
                        </Paragraph>
                        <Paragraph>
                            Het slaaponderzoek wordt elke ochtend automatisch verzonden naar Ksyos. De medisch
                            specialisten van Ksyos beoordelen het onderzoek zodra er voldoende succesvolle nachten zijn
                            binnengekomen. Binnen 2 weken wordt er contact met je opgenomen voor het inplannen van een
                            videoconsult met de medisch specialist.
                        </Paragraph>
                        <Paragraph>
                            De bevindingen uit het onderzoek en het consult zijn beiden noodzakelijk om tot een diagnose
                            te komen. In het videoconsult worden de diagnose en de behandelopties met je besproken.
                            Wanneer er een behandeling nodig is, zal Ksyos deze voor je in gang zetten.
                        </Paragraph>
                    </Content>
                </Article>
            </PageSection>
        </>
    );
}

export function OsasInstructions() {
    const [searchParams] = useSearchParams();
    const somnologyOrganizationType = searchParams.getOptional('somnologyOrganizationType') as
        SomnologyOrganizationType | undefined;

    return (
        <PageWrapper>
            <Header
                backButtonText={translateKey('common.home')}
                backUrl="/"
                hasBackbutton
                hasLogoutButton
            />
            <Main>
                <Container isFluidOnMobile>
                    {somnologyOrganizationType === SomnologyOrganizationType.ksyosMedicalCenter && ksyosInstructions()}
                    {somnologyOrganizationType !== SomnologyOrganizationType.ksyosMedicalCenter && nsiInstructions()}
                    <PageSection>
                        <Row>
                            <DeprecatedOsasFaqCard somnologyOrganizationType={somnologyOrganizationType ?? undefined} />
                        </Row>
                    </PageSection>
                </Container>
            </Main>
            <Footer />
        </PageWrapper>
    );
}
